import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import MapView from './components/MapView';
import GoogleMapView from './components/GoogleMapView';
import ReviewForm from './components/ReviewForm';
import BusinessDetails from './components/BusinessDetails';
import WelcomeModal from './components/WelcomeModal';
import AuthModal from './components/AuthModal';
import OnboardingWalkthrough from './components/OnboardingWalkthrough';
import DeepLinkHandler from './components/DeepLinkHandler';
import InterestSelection from './components/InterestSelection';
import PersonalizedFeed from './components/PersonalizedFeed';
import EnhancedBusinessProfile from './components/EnhancedBusinessProfile';
import BookmarkManager from './components/BookmarkManager';
import BusinessMembershipSignup from './components/BusinessMembershipSignup';
import BusinessDashboardPage from './pages/BusinessDashboardPage';
import { Business, UserInterest, UserPreferences, Organization } from './types';
import api, { bookmarkAPI, businessAPI, orgAPI, userAPI } from './services/api';

import AlertModal from './components/AlertModal';
import { List, Map, Navigation } from 'lucide-react';
import { GOOGLE_MAPS_CONFIG, isGoogleMapsConfigured } from './config/googleMaps';
import { POSITIVE_MIN_RATING } from './config/reviews';
import ProfileSettingPage from './pages/ProfileSettingPage';
import AdminPortalPage from './pages/AdminPortalPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailOtpModal from './components/VerifyEmailOtpModal';
import AddBusinessLocationModal from './components/AddBusinessLocationModal';
import FilteringEngine from './utils/filteringEngine';
import { loadStripe } from '@stripe/stripe-js';
import { CheckoutProvider } from '@stripe/react-stripe-js/checkout';
import CheckoutForm from './components/PaymentModal';
import CheckoutPayment from './components/PaymentModal';
import { EmbeddedCheckout, EmbeddedCheckoutProvider, PaymentElement } from '@stripe/react-stripe-js';
import ReturnPage from './components/ReturnPage';
import MapAreaEmptyState from './components/MapAreaEmptyState';
import {
  calculateRadiusFromZoom,
  filterBusinessesInBounds,
  MapBounds,
  radiusMilesFromBounds,
} from './utils/mapZoom';

// Simple Add Business Form Component
const AddBusinessForm: React.FC<{
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    category: '',
    phone: '',
    website: '',
    description: '',
    priceRange: '$'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.address.trim() || !formData.category.trim()) {
      alert('Please fill in the required fields: Business Name, Address, and Category.');
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };



  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Place Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter place name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address *
        </label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) => handleChange('address', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter address"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category *
        </label>
        <select
          value={formData.category}
          onChange={(e) => handleChange('category', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
        >
          <option value="">Select a category</option>
          <option value="Restaurant">Restaurant</option>
          <option value="Coffee Shop">Coffee Shop</option>
          <option value="Retail Store">Retail Store</option>
          <option value="Service">Service</option>
          <option value="Entertainment">Entertainment</option>
          <option value="Health & Wellness">Health & Wellness</option>
          <option value="Professional Services">Professional Services</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price Range
          </label>
          <select
            value={formData.priceRange}
            onChange={(e) => handleChange('priceRange', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="$">$ (Budget-friendly)</option>
            <option value="$$">$$ (Moderate)</option>
            <option value="$$$">$$$ (Expensive)</option>
            <option value="$$$$">$$$$ (Very Expensive)</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Website
        </label>
        <input
          type="url"
          value={formData.website}
          onChange={(e) => handleChange('website', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="https://www.example.com"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Brief description of the business..."
        />
      </div>

      <div className="flex space-x-3 pt-4">
        <button
          type="submit"
          className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
        >
          Add Place & Write Review
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

function App() {
  const [mobileMapViewMode, setMobileMapViewMode] = useState<'map' | 'list'>('map');
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showInterestSelection, setShowInterestSelection] = useState(false);
  const [showPersonalizedFeed, setShowPersonalizedFeed] = useState(false);
  const [showEnhancedProfile, setShowEnhancedProfile] = useState(false);
  const [showBookmarkManager, setShowBookmarkManager] = useState(false);
  const [showBusinessSignup, setShowBusinessSignup] = useState(false);
  const [showBusinessSelection, setShowBusinessSelection] = useState(false);
  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const [emailOtpPrompt, setEmailOtpPrompt] = useState<{
    token: string;
    email: string;
    codeAlreadySent: boolean;
    destinationMasked?: string;
    expiresInMinutes?: number;
    resendAvailableInSeconds?: number;
  } | null>(null);

  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('piezit-user');
    return saved ? JSON.parse(saved) : null;
  });


  const [organization, setOrganization] = useState<Organization | null>(null);
  const [bookmarkedBusinesses, setBookmarkedBusinesses] = useState<string[]>([]);
  const [favoritedBusinesses, setFavoritedBusinesses] = useState<string[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [backendConnected, setBackendConnected] = useState(false);

  // Trigger to refresh business reviews
  const [fetchBusinessReviews, setFetchBusinessReviews] = useState(0)

  // Pin dropping state
  const [isDroppingPin, setIsDroppingPin] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [confirmedPinLocation, setConfirmedPinLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showPinConfirmPrompt, setShowPinConfirmPrompt] = useState(false);

  // Alert modal state
  const [modal, setModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info' as 'success' | 'error' | 'info',
  });

  // Geolocation loading state
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoLoadingError, setGeoLoadingError] = useState(false);

  // Define level of rating for a positive review
  const positiveMinRating = POSITIVE_MIN_RATING;

  // Initial app setup
  useEffect(() => {
    const initializeApp = async () => {
      setLoading(true);
      // Test backend connection
      const isConnected = await api.testConnection();
      setBackendConnected(isConnected);
      // Google Maps drives the first fetch via onMapViewChange after geolocation.
      // Leaflet fallback has no viewport callback, so fetch once globally here.
      if (!isGoogleMapsConfigured()) {
        fetchBusinesses();
      }
      setLoading(false);
    };
    initializeApp();

    // console.log("organization  01:",organization);
  }, []);


  const lastMapViewRef = useRef<{
    center: { lat: number; lng: number };
    zoom: number;
    bounds?: MapBounds;
  } | null>(null);
  const mapZoomOutRef = useRef<(() => void) | null>(null);
  // True when the current viewport has no businesses inside the visible map.
  const [areaEmpty, setAreaEmpty] = useState(false);
  const [mapZoom, setMapZoom] = useState(GOOGLE_MAPS_CONFIG.defaultZoom);

  // fetch businesses for the current map viewport (clipped to visible bounds)
  const fetchBusinesses = useCallback(async (
    center?: { lat: number; lng: number },
    zoom?: number,
    bounds?: MapBounds,
  ) => {
    try {

      // fallback to last known view if params not provided, or use default center
      const view = center
        ? {
          center,
          zoom: zoom ?? GOOGLE_MAPS_CONFIG.defaultZoom,
          bounds: bounds ?? lastMapViewRef.current?.bounds,
        }
        : lastMapViewRef.current ?? {
          center: GOOGLE_MAPS_CONFIG.defaultCenter,
          zoom: GOOGLE_MAPS_CONFIG.defaultZoom,
        };

      lastMapViewRef.current = view;
      const currentZoom = view.zoom ?? GOOGLE_MAPS_CONFIG.defaultZoom;
      setMapZoom(currentZoom);

      // Prefer radius that covers the actual visible rectangle; zoom table is fallback only.
      const radius = view.bounds
        ? radiusMilesFromBounds(view.center, view.bounds)
        : calculateRadiusFromZoom(currentZoom);

      const allBusinessData = await api.business.getAllPositive(
        positiveMinRating,
        view.center?.lat,
        view.center?.lng,
        radius,
      );
      const businessData = Array.isArray(allBusinessData)
        ? allBusinessData
        : allBusinessData?.data;

      if (businessData && businessData.length > 0) {
        // Transform backend data to frontend format
        const transformedBusinesses = businessData.map((b: any) => ({
          id: b.id,
          name: b.name,
          address: b.address,
          latitude: parseFloat(b.latitude),
          longitude: parseFloat(b.longitude),
          category: b.category,
          interestCategories: typeof b.interest_categories === 'string'
            ? JSON.parse(b.interest_categories)
            : b.interest_categories,
          tags: typeof b.tags === 'string' && b.tags
            ? JSON.parse(b.tags)
            : (b.tags || []),
          priceRange: b.price_range,
          phone: b.phone,
          website: b.website,
          description: b.description,
          averageRating: parseFloat(b.average_rating) || 0,
          totalReviews: parseInt(b.total_reviews) || 0,
          features: typeof b.features === 'string' && b.features
            ? JSON.parse(b.features)
            : (b.features || []),
        }));

        // Strict clip: sidebar/markers only include what's inside the map frame.
        const visibleBusinesses = view.bounds
          ? filterBusinessesInBounds(transformedBusinesses, view.bounds)
          : transformedBusinesses;

        setBusinesses(visibleBusinesses);
        setAreaEmpty(visibleBusinesses.length === 0);
        console.log(
          '✅ Loaded',
          visibleBusinesses.length,
          `businesses in viewport (~${Math.round(radius)}mi query)`,
        );

        return visibleBusinesses;

      } else {
        // No businesses in this search area — clear so far-away spots don't linger.
        setBusinesses([]);
        setAreaEmpty(true);
        console.warn(`⚠️ No businesses within the current map view (~${Math.round(radius)}mi)`);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching businesses:', error);
      console.warn('⚠️ Using fallback data');
      // setBusinesses(getFallbackBusinesses());
    }
  }, [positiveMinRating]);

  const handleMapZoomOut = useCallback(() => {
    mapZoomOutRef.current?.();
  }, []);

  // Filter businesses based on user interests.
  // Filter businesses based on user interests when they have selected interests
  const filteredBusinesses = React.useMemo(() => {
    if (user && user.interests && user.interests.length > 0) {
      return FilteringEngine.filterBusinesses(businesses, {
        userInterests: user.interests,
        minimumRating: user.preferences?.minimumRating || 3.0,
        maxDistance: user.preferences?.maxDistance || 25,
        priceRange: user.preferences?.priceRange || [],
        showVerifiedOnly: user.preferences?.showVerifiedOnly || false
      });
    }
    return businesses;
  }, [businesses, user]);

  // Fallback businesses if backend is not available
  const getFallbackBusinesses = (): Business[] => [
    {
      id: '1',
      name: 'Artisan Coffee Roasters',
      address: '123 Main St, New York, NY 10001',
      latitude: 40.7128,
      longitude: -74.0060,
      category: 'Coffee Shop',
      interestCategories: ['food_dining'],
      tags: ['coffee', 'cozy', 'wifi'],
      priceRange: '$$',
      phone: '(555) 123-4567',
      website: 'www.artisancoffee.com',
      description: 'Premium coffee roasted fresh daily',
      averageRating: 4.8,
      totalReviews: 127,
      features: ['WiFi', 'Pet Friendly']
    }
  ];

  // Load user data, bookmarks, favorites, organization on app start or user change
  useEffect(() => {
    const loadBookmarks = async () => {
      if (!user?.id) return;
      try {
        const bookmarks = await api.bookmark.getByUser(user.id);
        if (bookmarks) {
          const ids = bookmarks as string[];
          setBookmarkedBusinesses(ids);
          localStorage.setItem('piezit-bookmarks', JSON.stringify(ids));
          // console.log('bookmarks: ', bookmarks);
        }
      } catch (err) {
        console.error('Error loading user data', err);
      }
    };
    loadBookmarks();

    // Load bookmarks and favorites
    const savedBookmarks = localStorage.getItem('piezit-bookmarks');
    if (savedBookmarks) {
      setBookmarkedBusinesses(JSON.parse(savedBookmarks));
    }

    const savedFavorites = localStorage.getItem('piezit-favorites');
    if (savedFavorites) {
      setFavoritedBusinesses(JSON.parse(savedFavorites));
    }

    // Load business member data
    const savedOrganization = localStorage.getItem('piezit-organization');
    if (savedOrganization) {
      setOrganization(JSON.parse(savedOrganization));
    }

    // Show welcome modal on first visit for non-users
    const hasVisited = localStorage.getItem('piezit-visited');
    if (!hasVisited && !user) {
      setShowWelcome(true);
      localStorage.setItem('piezit-visited', 'true');
    }

    // Show onboarding for new users
    const onboardingCompleted = localStorage.getItem('piezit-onboarding-completed');
    if (user && !onboardingCompleted && user.interests?.length === 0) {
      setTimeout(() => setShowOnboarding(true), 1000);
    }

    // console.log('user type: ', organization);
  }, [user]);

  // Handle successful authentication
  const handleAuthSuccess = (userData: any) => {
    setShowAuth(false);

    if (userData.type == 'user') {
      setUser(userData);

    } else if (userData.type == 'business') {
      setOrganization(userData);
      window.location.href = '/business/dashboard';
    }

    // Show onboarding for new users
    const onboardingCompleted = localStorage.getItem('piezit-onboarding-completed');
    if (!onboardingCompleted && userData.interests?.length === 0) {
      setTimeout(() => setShowOnboarding(true), 500);
    }
  };

  // Set user location using Geolocation API
  const setUserLocationWithGeo = (onDone: (coords: { lat: number; lng: number }) => void) => {
    const { defaultCenter } = GOOGLE_MAPS_CONFIG;
    if (!navigator?.geolocation) {
      onDone(defaultCenter);
      return;
    }

    setGeoLoadingError(false);
    setGeoLoading(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLoading(false);
        onDone({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        console.log('no location');
        setGeoLoading(false);
        setGeoLoadingError(true);
      },
      { enableHighAccuracy: true, timeout: 5000 }
    );
  };

  // Handle pin confirmation when dropping a pin
  const handleConfirmPin = (coordsOverride?: { lat: number; lng: number }) => {
    const coords = pendingLocation ?? coordsOverride;
    if (!coords) return;

    setConfirmedPinLocation(coords);
    setPendingLocation(null);
    setShowPinConfirmPrompt(false);
    setIsDroppingPin(false);

    setSelectedBusiness({
      id: 'temp',
      name: '',
      address: 'Custom location',
      latitude: coords.lat,
      longitude: coords.lng,
      category: 'Custom',
      interestCategories: [],
      tags: [],
      priceRange: '$',
      features: [],
      averageRating: 0,
      totalReviews: 0,
      isActive: true,
      isVerified: false,
    } as Business);

    setShowReviewForm(true);
  };

  // Handle pin drop cancellation from user
  const handleCancelPinDrop = () => {
    setPendingLocation(null);
    setShowPinConfirmPrompt(false);
    setIsDroppingPin(false);
    setShowBusinessSelection(true);
  };

  // Handle pin drop toggle
  const handlePinDropToggle = () => {
    setIsDroppingPin(true);
    setShowBusinessSelection(false);
    setUserLocationWithGeo((coords) => {
      setPendingLocation(coords);
      setShowPinConfirmPrompt(true);
    });

  };

  const handlePinDrop = (coords: { lat: number; lng: number }) => {
    setPendingLocation(coords);
    setShowPinConfirmPrompt(true);
  };

  // Handle "My Location" click button
  const handleMyLocationClick = () => {
    setShowBusinessSelection(false);
    setUserLocationWithGeo((coords) => {
      handleConfirmPin(coords);
      setPendingLocation(coords);
    });
  };

  // Handle business selection from list or map and increment view count
  const handleBusinessSelect = useCallback(async (business: Business) => {
    if (user) {
      try {
        const res = await businessAPI.incrementViewCount(user.id, business.id);
        // if (res) {
        //   console.log('View count incremented for business:', res);
        // }
      } catch (err) {
        console.error('Error incrementing view count: ', err);
      }
    } else {
      console.log('No user logged in, cannot increment view count.');
    }
    setPendingLocation(null);
    setShowPinConfirmPrompt(false);
    setIsDroppingPin(false);
    setMobileMapViewMode('list');
    setSelectedBusiness(business);
  }, [user]);

  const handleBusinessDeselect = useCallback(() => {
    setSelectedBusiness(null);
    setMobileMapViewMode('map');
  }, []);

  // const handleViewFullProfile = (business: Business) => {
  //   setSelectedBusiness(business);
  //   setShowEnhancedProfile(false); // Close modal first
  //   // The side panel will now show the full BusinessDetails
  // };

  // Handle bookmark click to add/remove
  const handleBookmark = async (businessId: string) => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    try {
      if (bookmarkedBusinesses.includes(businessId)) {
        await bookmarkAPI.remove(user.id, businessId);
      } else {
        await bookmarkAPI.add(user.id, businessId);
      }
      handleBookmarkUpdate();
    } catch (err) {
      console.error(err);
      setBookmarkedBusinesses(bookmarkedBusinesses);
      localStorage.setItem('piezit-bookmarks', JSON.stringify(bookmarkedBusinesses));
    }
  };

  // Handle bookmark updates after add/remove
  const handleBookmarkUpdate = async () => {
    if (user) {
      const updatedBookmark = await bookmarkAPI.getByUser(user.id);
      if (updatedBookmark) {
        const updated = await bookmarkAPI.getByUser(user.id);
        const ids = Array.isArray(updated) ? updated : [];
        setBookmarkedBusinesses(ids);
        localStorage.setItem('piezit-bookmarks', JSON.stringify(ids));
      }
    }
  };

  // no functionality yet, it's same as bookmark for now
  const handleFavorite = (businessId: string) => {
    const newFavorites = favoritedBusinesses.includes(businessId)
      ? favoritedBusinesses.filter(id => id !== businessId)
      : [...favoritedBusinesses, businessId];

    setFavoritedBusinesses(newFavorites);
    localStorage.setItem('piezit-favorites', JSON.stringify(newFavorites));
  };

  // create organization after signup
  const handleBusinessSignupComplete = async (businessData: any) => {

    try {
      const newOrganization: Organization = {
        id: Date.now().toString(),
        orgName: businessData.orgName,
        ownerName: businessData.ownerName,
        email: businessData.email,
        phone: businessData.phone,
        website: businessData.website || '',
        description: businessData.description,
        category: businessData.category,
        businessLocations: businessData.location || [],
        membershipPlan: businessData.membershipPlan,
        subscriptionDate: new Date(),
        isActive: true,
        totalMonthlyFee: businessData.monthlyFee,
        analytics: {
          totalReviews: 0,
          averageRating: 0,
          monthlyReviews: 0,
          reviewTrends: [],
          topKeywords: [],
          responseRate: 0,
          viewCount: 0,
          bookmarkCount: 0,
          favoriteCount: 0,
          ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
        }
      };

      const updatedBiz = {
        ...newOrganization,
        password: businessData.password
      };
      setOrganization(newOrganization);

      console.log("create biz member: ", updatedBiz);

      // const response = await orgAPI.register(updatedBiz);
      // if(response){
      //   // check any error and alert
      // }
      // localStorage.setItem('piezit-organization', JSON.stringify(response.org));
      // localStorage.setItem('piezit-org-token', response.token);

      const response: any = await orgAPI.register(updatedBiz);

      // Some backends may return { error: "..."} with a 200; handle that too.
      const backendError = response?.error || response?.message;
      if (backendError) {
        const msg = String(backendError);
        alert(msg);
        setModal({
          open: true,
          title: 'Registration failed',
          message: msg,
          variant: 'error',
        });
        return;
      }

      if (!response?.org || !response?.token) {
        const msg = 'Business registration failed: invalid server response';
        alert(msg);
        setModal({
          open: true,
          title: 'Registration failed',
          message: msg,
          variant: 'error',
        });
        return;
      }

      // setOrganization(response.org ?? newOrganization);
      localStorage.setItem('piezit-organization', JSON.stringify(response.org ?? newOrganization));
      localStorage.setItem('piezit-org-token', response.token);
      setOrganization(response.org ?? newOrganization);
      setShowBusinessSignup(false);

      if (response.requiresEmailVerification && response.token) {
        setEmailOtpPrompt({
          token: response.token,
          email: response.org?.email || businessData.email,
          codeAlreadySent: Boolean(response.otp),
          destinationMasked: response.otp?.destinationMasked,
          expiresInMinutes: response.otp?.expiresInMinutes,
          resendAvailableInSeconds: response.otp?.resendAvailableInSeconds,
        });
        return;
      }

      window.location.href = '/business/dashboard';
    } catch (error: any) {
      console.error('Error registering business:', error);
      const msg = error?.message || 'Business registration failed. Please try again.';
      alert(msg);
      setModal({
        open: true,
        title: 'Registration failed',
        message: msg,
        variant: 'error',
      });
    }

    // setShowBusinessSignup(false);
    // Navigate to business dashboard page
    // window.location.href = '/business/dashboard';
  };


  // on start review click
  const handleStartReview = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    // option 1: If no business is selected, show business selection modal 
    if (!selectedBusiness) {
      setShowBusinessSelection(true);
      return;
    }
    setShowReviewForm(true);

    // option 2: always open list of business
    // setShowBusinessSelection(true);
  };

  // Handle "Show Some Love" from map marker info window - selects business and opens review form
  const handleShowSomeLoveFromMap = (business: Business) => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    setSelectedBusiness(business);
    setShowReviewForm(true);
  };

  // close review form and reset all related state
  const handleCloseReview = () => {
    setShowReviewForm(false);
    setPendingLocation(null);
    setConfirmedPinLocation(null);
  };

  // submit review
  const handleReviewSubmit = async (reviewData: any) => {
    try {
      if (!user) {
        setModal({
          open: true,
          title: 'Sorry!',
          message: 'Please login to submit a review',
          variant: 'error',
        });
        return;
      }

      if (!selectedBusiness) {
        setModal({
          open: true,
          title: 'Sorry!',
          message: 'Please select a business first',
          variant: 'error',
        });
        return;
      }

      // console.log('Submitting review:', reviewData);

      // Call API to create review
      const response = await api.review.create({
        businessId: selectedBusiness.id,
        userId: user.id,
        title: reviewData.title,
        content: reviewData.content,
        rating: reviewData.rating,
        aspects: reviewData.aspects || [],
        photos: reviewData.photos || [],
        isAnonymous: reviewData.isAnonymous,
      });

      console.log('✅ Review created successfully:', response);

      // refresh business data to get updated average rating
      const updateBusinessData: Business[] = await fetchBusinesses();
      const updated = selectedBusiness
        ? updateBusinessData?.find((b: Business) => b.id === selectedBusiness.id)
        : null;
      if (updated) setSelectedBusiness(updated);

      // Trigger BusinessDetails to re-fetch reviews
      setFetchBusinessReviews(prev => prev + 1);

      return response;

    } catch (error: any) {
      console.error('❌ Error submitting review:', error);
      setModal({
        open: true,
        title: 'Sorry!',
        message: error.message || 'Failed to submit review. Please try again.',
        variant: 'error',
      });
      throw error;
    }
  };


  const handleAddNewBusiness = async (businessData: any) => {
    try {
      // console.log('Creating new business:', businessData);
      // Call API to create business in database
      const response = await api.business.create({
        name: businessData.name,
        address: businessData.address,
        category: businessData.category,
        latitude: businessData.latitude || 40.7128,
        longitude: businessData.longitude || -74.0060,
        // Let the backend map the free-text category onto the canonical
        // InterestCategory enum (via normalizeBusinessInterestCategories).
        // Forward any explicitly-selected enum categories if present.
        interestCategories: businessData.interestCategories || [],
        tags: businessData.tags || [],
        priceRange: businessData.priceRange || '$',
        phone: businessData.phone || '',
        website: businessData.website || '',
        description: businessData.description || '',
        features: businessData.features || [],
      });

      console.log('✅ Business created successfully:', response);

      // Add to local businesses array
      const newBusiness = response.business;
      setBusinesses(prev => [...prev, newBusiness]);

      // Set as selected and proceed with review
      setSelectedBusiness(newBusiness);
      setShowAddBusiness(false);
      setShowBusinessSelection(false);
      // setShowReviewForm(true);

      // alert('Business added successfully! Now you can write your review.');
      setModal({
        open: true,
        title: 'Business added successfully!',
        message: 'Now you can write your review.',
        variant: 'success',
      });
    } catch (error: any) {
      console.error('❌ Error creating business:', error);
      alert(error.message || 'Failed to create business. Please try again.');
      setModal({
        open: true,
        title: 'Sorry!',
        message: error.message || 'Failed to create business. Please try again.',
        variant: 'error',
      });
    }
  };

  // get updated user profile
  const handleUserUpdate = async () => {
    const token = localStorage.getItem('piezit-token');
    if (token) {
      const updatedUser = await userAPI.getProfile(token);
      if (updatedUser?.user) {
        console.log('updatedUser: ', updatedUser.user);
        setUser(updatedUser.user);
        localStorage.setItem('piezit-user', JSON.stringify(updatedUser.user));
      }
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Show interest selection after onboarding
    if (user && (!user.interests || user.interests.length === 0)) {
      setTimeout(() => setShowInterestSelection(true), 500);
    }
  };

  const handleInterestsComplete = async (interests: UserInterest[]) => {
    if (user) {
      try {
        await api.user.saveInterests({
          userId: user.id,
          interests: { interests },
        });
        await handleUserUpdate();
      } catch (error: any) {
        setModal({
          open: true,
          title: 'Sorry!',
          message: error.message || 'Failed to save interests. Please try again.',
          variant: 'error',
        });
        return;
      }
    }
    setShowInterestSelection(false);
  };

  const handleShowPersonalizedFeed = () => {
    setShowPersonalizedFeed(true);
  };

  const handleUpdateInterests = () => {
    setShowInterestSelection(true);
  };

  const handlePreferencesComplete = async (preferences: UserPreferences) => {
    console.info('preferences updated: ', preferences);
    try {
      await api.user.updatePreferences({
        userId: user.id,
        preferences: preferences
      });
      await handleUserUpdate();
    } catch (error: any) {
      setModal({
        open: true,
        title: 'Sorry!',
        message: error.message || 'Failed to save preferences. Please try again.',
        variant: 'error',
      });
    }
  }



  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Header
          user={user}
          businessMember={organization}
          onAuthClick={() => setShowAuth(true)}
          onShowPersonalizedFeed={handleShowPersonalizedFeed}
          onShowBusinessSignup={() => setShowBusinessSignup(true)}
          onStartReview={handleStartReview}
        />

        <Routes>
          <Route path="/" element={
            <main className="flex flex-col md:flex-row h-[calc(100vh-4rem)] overflow-hidden">
              <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3">
                <div className="max-w-md mx-auto bg-gray-100 rounded-lg p-1 flex items-center">
                  <button
                    onClick={() => setMobileMapViewMode('map')}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                      mobileMapViewMode === 'map'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600'
                    }`}
                  >
                    <Map className="w-4 h-4" />
                    <span>Map</span>
                  </button>
                  <button
                    onClick={() => setMobileMapViewMode('list')}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                      mobileMapViewMode === 'list'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600'
                    }`}
                  >
                    <List className="w-4 h-4" />
                    <span>List</span>
                  </button>
                </div>
              </div>
              <DeepLinkHandler
                businesses={businesses}
                onBusinessSelect={handleBusinessSelect}
              />
              {/* Map Section */}
              <div className={`relative md:flex-1 md:h-full ${
                mobileMapViewMode === 'list' ? 'hidden md:block' : 'flex-1'
              }`}>
                {isGoogleMapsConfigured() ? (
                  <GoogleMapView
                    onBusinessSelect={handleBusinessSelect}
                    onBusinessDeselect={handleBusinessDeselect}
                    selectedBusiness={selectedBusiness}
                    businesses={filteredBusinesses}
                    user={user}
                    isDroppingPin={isDroppingPin}
                    pendingPinLocation={pendingLocation}
                    onPinDrop={handlePinDrop}
                    onCancelPinDrop={handleCancelPinDrop}
                    showPinConfirmPrompt={showPinConfirmPrompt}
                    onConfirmPin={handleConfirmPin}
                    onMapViewChange={fetchBusinesses}
                    onStartReview={handleShowSomeLoveFromMap}
                    onLeaveReviewClick={handleStartReview}
                    zoomOutRef={mapZoomOutRef}
                  />
                ) : (
                  <MapView
                    onBusinessSelect={handleBusinessSelect}
                    selectedBusiness={selectedBusiness}
                    businesses={filteredBusinesses}
                    user={user}
                  />
                )}

              </div>

              {/* Side Panel */}
              {!showPinConfirmPrompt && (
                <div className={`w-full md:w-96 z-20 bg-white shadow-lg border-l border-gray-200 flex flex-col md:h-full ${
                  mobileMapViewMode === 'map' ? 'hidden md:flex' : 'flex-1 md:flex-none min-h-0 overflow-y-auto'
                }`}>
                  {selectedBusiness ? (
                    <BusinessDetails
                      business={selectedBusiness}
                      user={user}
                      onStartReview={handleStartReview}
                      onClose={() => { setSelectedBusiness(null); setMobileMapViewMode('map'); }}
                      onViewFullProfile={() => setShowEnhancedProfile(true)}
                      onUserUpdate={handleUserUpdate}
                      onRefresh={fetchBusinessReviews}
                      onBookmark={handleBookmark}
                      isBookmarked={bookmarkedBusinesses.includes(selectedBusiness.id)}
                      positiveMinRating={positiveMinRating}
                    />
                  ) : (
                    <>
                      {filteredBusinesses && filteredBusinesses.length == 0 ? (
                        <div className="flex-1 flex items-center justify-center p-6">
                          {areaEmpty ? (
                            <MapAreaEmptyState
                              canZoomOut={mapZoom > 3}
                              onZoomOut={handleMapZoomOut}
                              onLeaveReview={handleStartReview}
                            />
                          ) : (
                            <div className="text-center">
                              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Discover Amazing Businesses
                              </h3>
                              <p className="text-gray-600 text-sm">
                                Click on any business marker on the map to see details and show some love!
                              </p>
                            </div>
                          )}
                        </div>) : (
                        <div className="flex-1 flex justify-center p-6 pb-8 md:mb-6 md:overflow-y-auto">
                          <div className="">
                            <div className=' space-y-4'>
                              <div>Result({filteredBusinesses.length})</div>
                              {filteredBusinesses.map((business) => (
                                <button
                                  key={business.id}
                                  onClick={() => {
                                    setSelectedBusiness(business);
                                    handleBusinessSelect(business);
                                    setShowBusinessSelection(false);
                                    setShowReviewForm(true); // auto open form
                                  }}
                                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                >
                                  <div className="flex items-start space-x-4">
                                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                      <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="font-medium text-gray-900">{business.name}</h3>
                                      <p className="text-sm text-gray-600">{business.category}</p>
                                      <p className="text-sm text-gray-500">{business.address}</p>
                                      <div className="flex items-center space-x-2 mt-2">
                                        <div className="flex items-center space-x-1">
                                          {Array.from({ length: 5 }, (_, i) => (
                                            <svg
                                              key={i}
                                              className={`w-4 h-4 ${i < Math.floor(business.averageRating || 0)
                                                ? 'text-yellow-400 fill-current'
                                                : 'text-gray-300'
                                                }`}
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                          ))}
                                        </div>
                                        <span className="text-sm text-gray-600">{business.averageRating || 0} ({business.totalReviews || 0} reviews)</span>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              ))}




                            </div>
                          </div>
                        </div>)}
                    </>

                  )}
                </div>)}

              {/* Review Form Modal */}
              {showReviewForm && selectedBusiness && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <ReviewForm
                      business={selectedBusiness}
                      onSubmit={handleReviewSubmit}
                      onClose={handleCloseReview}
                    />
                  </div>
                </div>
              )}
            </main>
          } />
          <Route path="/business/:businessId" element={
            <main className="flex h-[calc(100vh-4rem)]">
              <DeepLinkHandler
                businesses={businesses}
                onBusinessSelect={handleBusinessSelect}
              />
              {/* Map Section */}
              <div className="flex-1 relative">
                {isGoogleMapsConfigured() ? (
                  <GoogleMapView
                    onBusinessSelect={handleBusinessSelect}
                    onBusinessDeselect={handleBusinessDeselect}
                    selectedBusiness={selectedBusiness}
                    businesses={filteredBusinesses}
                    user={user}
                    isDroppingPin={isDroppingPin}
                    pendingPinLocation={pendingLocation}
                    onPinDrop={handlePinDrop}
                    onCancelPinDrop={handleCancelPinDrop}
                    showPinConfirmPrompt={showPinConfirmPrompt}
                    onConfirmPin={handleConfirmPin}
                    onMapViewChange={fetchBusinesses}
                    onStartReview={handleShowSomeLoveFromMap}
                    onLeaveReviewClick={handleStartReview}
                    zoomOutRef={mapZoomOutRef}
                  />
                ) : (
                  <MapView
                    onBusinessSelect={handleBusinessSelect}
                    selectedBusiness={selectedBusiness}
                    businesses={filteredBusinesses}
                    user={user}
                  />
                )}
              </div>

              {/* Side Panel */}
              <div className="w-96 bg-white shadow-lg border-l border-gray-200 flex flex-col">
                {selectedBusiness ? (
                  <BusinessDetails
                    business={selectedBusiness}
                    user={user}
                    onStartReview={handleStartReview}
                    onClose={() => { setSelectedBusiness(null); setMobileMapViewMode('map'); }}
                    onViewFullProfile={() => setShowEnhancedProfile(true)}
                    onUserUpdate={handleUserUpdate}
                    positiveMinRating={positiveMinRating}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center p-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Business Loading...
                      </h3>
                      <p className="text-gray-600 text-sm">
                        We're finding the business you're looking for!
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Review Form Modal */}
              {showReviewForm && selectedBusiness && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <ReviewForm
                      business={selectedBusiness}
                      onSubmit={handleReviewSubmit}
                      onClose={handleCloseReview}
                    />
                  </div>
                </div>
              )}
            </main>
          } />
          <Route path="/business/:businessId/review/:reviewId" element={
            <main className="flex h-[calc(100vh-4rem)]">
              <DeepLinkHandler
                businesses={businesses}
                onBusinessSelect={handleBusinessSelect}
              />
              {/* Map Section */}
              <div className="flex-1 relative">
                {isGoogleMapsConfigured() ? (
                  <GoogleMapView
                    onBusinessSelect={handleBusinessSelect}
                    onBusinessDeselect={handleBusinessDeselect}
                    selectedBusiness={selectedBusiness}
                    businesses={filteredBusinesses}
                    user={user}
                    isDroppingPin={isDroppingPin}
                    pendingPinLocation={pendingLocation}
                    onPinDrop={handlePinDrop}
                    onCancelPinDrop={handleCancelPinDrop}
                    showPinConfirmPrompt={showPinConfirmPrompt}
                    onConfirmPin={handleConfirmPin}
                    onMapViewChange={fetchBusinesses}
                    onStartReview={handleShowSomeLoveFromMap}
                    onLeaveReviewClick={handleStartReview}
                    zoomOutRef={mapZoomOutRef}
                  />
                ) : (
                  <MapView
                    onBusinessSelect={handleBusinessSelect}
                    selectedBusiness={selectedBusiness}
                    businesses={filteredBusinesses}
                    user={user}
                  />
                )}
              </div>

              {/* Side Panel */}
              <div className="w-96 bg-white shadow-lg border-l border-gray-200 flex flex-col">
                {selectedBusiness && !showEnhancedProfile ? (
                  <BusinessDetails
                    business={selectedBusiness}
                    user={user}
                    onStartReview={handleStartReview}
                    onClose={() => { setSelectedBusiness(null); setMobileMapViewMode('map'); }}
                    onViewFullProfile={() => setShowEnhancedProfile(true)}
                    onUserUpdate={handleUserUpdate}
                    positiveMinRating={positiveMinRating}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center p-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Discover Businesses
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Click on map markers to explore businesses and read reviews!
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </main>
          } />
          <Route path="/business/dashboard" element={<BusinessDashboardPage />} />
          <Route path="/return" element={<ReturnPage />} />
          <Route path="/user" element={
            <main className="flex flex-col md:flex-row min-h-[calc(100vh-4rem)] bg-white">
              <ProfileSettingPage
                user={user}
                onEdit={handlePreferencesComplete}
                onInterestsEdit={() => setShowInterestSelection(true)}
                onPreferencesEdit={() => setShowPersonalizedFeed(true)} />
            </main>} />
          <Route path="/admin" element={<AdminPortalPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>

        {/* Welcome Modal */}
        {showWelcome && (
          <WelcomeModal onClose={() => setShowWelcome(false)} />
        )}

        {/* Auth Modal */}
        {showAuth && (
          <AuthModal
            isOpen={showAuth}
            onClose={() => setShowAuth(false)}
            onAuthSuccess={handleAuthSuccess}
          />
        )}

        {emailOtpPrompt && (
          <VerifyEmailOtpModal
            isOpen
            email={emailOtpPrompt.email}
            token={emailOtpPrompt.token}
            codeAlreadySent={emailOtpPrompt.codeAlreadySent}
            destinationMasked={emailOtpPrompt.destinationMasked}
            expiresInMinutes={emailOtpPrompt.expiresInMinutes}
            initialResendCooldownSeconds={emailOtpPrompt.resendAvailableInSeconds}
            allowSkip
            onVerified={() => {
              const stored = localStorage.getItem('piezit-organization');
              if (stored) {
                try {
                  const parsed = JSON.parse(stored);
                  parsed.verified = true;
                  localStorage.setItem('piezit-organization', JSON.stringify(parsed));
                  setOrganization(parsed);
                } catch {
                  /* ignore */
                }
              }
              setEmailOtpPrompt(null);
              window.location.href = '/business/dashboard';
            }}
            onSkip={() => {
              setEmailOtpPrompt(null);
              window.location.href = '/business/dashboard';
            }}
            onClose={() => {
              setEmailOtpPrompt(null);
              window.location.href = '/business/dashboard';
            }}
          />
        )}

        {/* Alert Modal */}
        <AlertModal
          open={modal.open}
          title={modal.title}
          message={modal.message}
          variant={modal.variant}
          onClose={() => setModal(prev => ({ ...prev, open: false }))}
        />

        {geoLoading && (
          <AlertModal
            open={true}
            title="Getting your location"
            message="One moment while we fetch your position..."
            variant="info"
            confirmLabel='Cancel'
            onClose={() => setGeoLoading(false)}
          />
        )}

        {geoLoadingError && (
          <AlertModal
            open={true}
            title="Cannot loading location"
            message="Please try again"
            variant="error"
            confirmLabel="Retry"
            onClose={() => setGeoLoadingError(false)}
            onButton={handleMyLocationClick}
          />
        )}

        {/* Onboarding Walkthrough */}
        {showOnboarding && user && (
          <OnboardingWalkthrough
            isOpen={showOnboarding}
            onClose={() => setShowOnboarding(false)}
            onComplete={handleOnboardingComplete}
            user={user}
          />
        )}



        {/* Personalized Feed Modal */}
        {showPersonalizedFeed && user && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Your Personalized Feed</h2>
                <button
                  onClick={() => setShowPersonalizedFeed(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  ✕
                </button>
              </div>
              <div className="p-6">
                <PersonalizedFeed
                  user={user}
                  businesses={businesses}
                  reviews={[]} // You would fetch reviews here in a real app
                  onBusinessSelect={(business) => {
                    setSelectedBusiness(business);
                    setShowPersonalizedFeed(false);
                  }}
                  onUpdateInterests={handleUpdateInterests}
                  onUpdatePreferences={handlePreferencesComplete}
                />
              </div>
            </div>
          </div>
        )}

        {/* Interest Selection Modal */}
        {showInterestSelection && (
          <InterestSelection
            isOpen={showInterestSelection}
            onClose={() => setShowInterestSelection(false)}
            onComplete={handleInterestsComplete}
            currentInterests={user?.interests || []}
            title={user?.interests?.length > 0 ? "Update Your Interests" : "What interests you?"}
            isOnboarding={!user?.interests || user.interests.length === 0}
          />
        )}

        {/* Enhanced Business Profile Modal */}
        {showEnhancedProfile && selectedBusiness && (
          <EnhancedBusinessProfile
            business={selectedBusiness}
            user={user}
            onClose={() => {
              setShowEnhancedProfile(false);
              setSelectedBusiness(null);
              setMobileMapViewMode('map');
            }}
            onStartReview={() => {
              setShowEnhancedProfile(false);
              handleStartReview();
            }}
            onViewFullProfile={() => {
              setShowEnhancedProfile(false);
              // Keep selectedBusiness set so it shows in the side panel
            }}
            onBookmark={handleBookmark}
            onFavorite={handleFavorite}
            isBookmarked={bookmarkedBusinesses.includes(selectedBusiness.id)}
            isFavorited={favoritedBusinesses.includes(selectedBusiness.id)}
            positiveMinRating={positiveMinRating}
          />
        )}

        {/* Bookmark Manager Modal */}
        {showBookmarkManager && user && (
          <BookmarkManager
            user={user}
            businesses={businesses}
            onClose={() => setShowBookmarkManager(false)}
            onViewBusiness={(business) => {
              setSelectedBusiness(business);
              setShowBookmarkManager(false);
              setShowEnhancedProfile(true);
            }}
          />
        )}

        {/* Business Membership Signup Modal */}
        {showBusinessSignup && (
          <BusinessMembershipSignup
            onClose={() => setShowBusinessSignup(false)}
            onSignupComplete={handleBusinessSignupComplete}
          />
        )}

        {/* Business Selection Modal for Review */}
        {showBusinessSelection && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[70vh] md:max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Select a Place to Review</h2>
                <button
                  onClick={() => setShowBusinessSelection(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  ✕
                </button>
              </div>
              <div className="p-6">
                <div className='mb-6 md:flex space-y-2 md:space-y-0 gap-4'>
                  <button
                    className="w-full btn-secondary font-medium flex items-center justify-center gap-2"
                    onClick={handleMyLocationClick}
                  >
                    <Navigation className="w-4 h-4" />
                    <span>My current location</span>
                  </button>
                  <button
                    className="w-full btn-secondary bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors duration-200 font-medium"
                    onClick={handlePinDropToggle}>
                    Select location on map
                  </button>
                </div>

                <div className="space-y-4">
                  {filteredBusinesses.map((business) => (
                    <button
                      key={business.id}
                      onClick={() => {
                        setSelectedBusiness(business);
                        handleBusinessSelect(business);
                        setShowBusinessSelection(false);
                        setShowReviewForm(true); // auto open form
                      }}
                      className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{business.name}</h3>
                          <p className="text-sm text-gray-600">{business.category}</p>
                          <p className="text-sm text-gray-500">{business.address}</p>
                          <div className="flex items-center space-x-2 mt-2">
                            <div className="flex items-center space-x-1">
                              {Array.from({ length: 5 }, (_, i) => (
                                <svg
                                  key={i}
                                  className={`w-4 h-4 ${i < Math.floor(business.averageRating || 0)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                    }`}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            <span className="text-sm text-gray-600">{business.averageRating || 0} ({business.totalReviews || 0} reviews)</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-6 space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-4">
                      Can't find the place you're looking for?
                    </p>
                    <button
                      onClick={() => {
                        setShowBusinessSelection(false);
                        setShowAddBusiness(true);
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
                    >
                      Add New Place
                    </button>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">
                      Or click on the map to explore more places.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add New Business Modal */}
        {showAddBusiness && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Add New Location</h2>
                <button
                  onClick={() => setShowAddBusiness(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  ✕
                </button>
              </div>
              <div className="p-6">
                {/* <AddBusinessForm
                  onSubmit={handleAddNewBusiness}
                  onCancel={() => setShowAddBusiness(false)}
                /> */}
                <AddBusinessLocationModal
                  isOpen={showAddBusiness}
                  onCancel={() => setShowAddBusiness(false)}
                  onSubmit={handleAddNewBusiness}
                  userType='user'
                />
              </div>
            </div>
          </div>
        )}

      </div>
    </Router>
  );
}

export default App;
